import { CanadaPostClient } from "@/Common/Api/CanadaPost/CanadaPostClient";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";

import styles from "./SearchAddress.module.scss";

import img_icon_topbar_right_arrow from "@/Globals/_images/icons/topbar_right_arrow.png";

interface SearchedAddressResult {
    streetAddress: string,
    city: string,
    postalCode: string,
    stateCode: string,
    countryCode: string
}

interface ISearchAddressProps {
    onItemSelectCallback: (address: SearchedAddressResult) => void;
    onTextChangeCallback: (address: string) => void;
    countryCode: string;
    debounce: number;
    streetAddress?: string;
    style?: React.CSSProperties;
    maxLength?: number;

    onBlur?: () => void;

    needValidation?: boolean;
    dataCy?: string;
    isInvalid?: boolean;

    isDisabled?: boolean;
}

class SearchAddressState {
    public id: string;
    public forceHideList: boolean;
    public text: string;
    public list: Immutable.List<any>;

    constructor(s: SearchAddressState) {
        if (s != null) {
            this.id = s.id;
            this.forceHideList = s.forceHideList;
            this.text = s.text;
            this.list = s.list;
        }
    }
}

class SearchAddressResponse {
    // pl says to pl:  do not refactor, this comes from canada post
    public Id: string;
    public Text: string;
    public Description: string;
    public Next: string;
}

class SearchAddressListItem {
    public Active: boolean;
    public model: SearchAddressResponse;

    constructor(model: SearchAddressResponse) {
        this.model = model;
        this.Active = false;
    }
}

class SearchAddress extends BaseReactComponent<ISearchAddressProps, SearchAddressState> {
    private timeoutReferenceSearch: number;
    private timeoutReferenceTextChange: number;
    private orderedResponseId: number = 0;

    public constructor(props: ISearchAddressProps) {
        super(props);
        this.state = new SearchAddressState({
            forceHideList: true,
            text: (props.streetAddress != null) ? this.props.streetAddress : "",
            list: Immutable.List([]),
            id: sessionStore.newId().toString()
        });
        this.timeoutReferenceSearch = -1;
    }

    public componentDidUpdate(prevProps: ISearchAddressProps): void {
        if (prevProps.streetAddress !== this.props.streetAddress) {
            this.mutateState(state => state.text = this.props.streetAddress);
        }
    }

    public render(): JSX.Element {
        let classError = "";
        if (this.props.isInvalid || (this.props.needValidation && IsNullOrWhiteSpace(this.state.text))) {
            classError = "has-error";
        }

        return (
            <div style={{ position: "relative" }} className={classError}>
                <input
                    data-cy={this.props.dataCy}
                    maxLength={this.props.maxLength}
                    id={this.state.id}
                    key={this.state.id}
                    style={this.props.style}
                    onClick={(event: React.MouseEvent) => this.onClick(event)}
                    onBlur={() => this.onBlur()}
                    onFocus={() => this.onFocus()}
                    type="text"
                    onChange={(e: any) => this.onTextChange(e.target.value)}
                    onKeyDown={(event: React.KeyboardEvent) => this.inputKeyDown(event)}
                    value={this.state.text}
                    className="form-control"
                    disabled={this.props.isDisabled}
                />
                <div className={styles.searchResult}>
                    {this.state.list.size > 0 && this.state.forceHideList != true && (
                        <div
                            className={styles.resultContainer}
                            onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        >
                            <ul className={styles.resultList}>
                                {this.state.list.map((listItem: SearchAddressListItem) => this.renderResponse(listItem))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderResponse(item: SearchAddressListItem): JSX.Element {
        let text = item.model.Text;
        let description = item.model.Description;

        const resultItemClassName = item.Active ? styles.resultItemActive : styles.resultItem;
        return (
            <li key={item.model.Id + "__code_"}>
                <div
                    className={resultItemClassName}
                    onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
                    onKeyDown={(event: React.KeyboardEvent) => this.inputKeyDown(event)}
                    onClick={() => this.selectItem(item)}
                >
                    <div className={styles.resultItemDescription}>
                        <span>{text}</span>
                        <div>
                            <span>{description}</span>
                        </div>
                    </div>

                    <div className={styles.resultItemDetails}>
                        {item.model.Next == "Find" && (
                            <div>
                                <img
                                    id={item.model.Id + "_handle_details"}
                                    src={img_icon_topbar_right_arrow}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </li>
        );
    }

    private onTextChange(value: string): void {
        this.mutateState(s => {
            s.forceHideList = false;
            s.text = value;
        });

        if (this.timeoutReferenceTextChange !== -1) {
            clearTimeout(this.timeoutReferenceTextChange);
            this.timeoutReferenceTextChange = -1;
        }

        this.timeoutReferenceTextChange = window.setTimeout(
            () => this.props.onTextChangeCallback(value),
            this.props.debounce
        );

        this.loadData(value, false);// has Its own debounce
    }

    private setForceHide(hide: boolean = true): void {
        this.mutateState(s => {
            s.forceHideList = hide;
        });
    }

    private inputKeyDown(e: React.KeyboardEvent): void {
        // up
        if (e.keyCode === 38) { e.preventDefault(); this.moveSelectionUpOrDown(true); }

        // down
        if (e.keyCode === 40) { e.preventDefault(); this.moveSelectionUpOrDown(false); }

        // enter
        if (e.keyCode === 13) {
            if (this.state.forceHideList == true || this.state.list == null || this.state.list.count() == 0) {
                this.setForceHide(false);
                this.loadData(this.state.text, false);
            }
            else
                this.selectActiveItem();
        }

        // escape
        if (e.keyCode === 27) {
            this.setForceHide(true);
        }
    }

    private moveSelectionUpOrDown(isDirectionUp: boolean): void {
        let collection = this.state.list.toArray();
        if (collection.length == 0)
            return;

        let idx = this.indexOfSelectedItem();
        if (idx === -1) {
            collection.forEach(x => x.Active = false);
            collection[isDirectionUp ? collection.length - 1 : 0].Active = true;
            this.updateStateWithNewList(collection);
            return;
        }

        if ((idx === 0 && isDirectionUp) || (idx === collection.length - 1 && !isDirectionUp)) {
            collection[isDirectionUp ? 0 : collection.length - 1].Active = false;
            this.updateStateWithNewList(collection);
            return;
        }

        collection[idx].Active = false;
        idx = idx + (isDirectionUp ? -1 : 1);

        collection[idx].Active = true;
        this.updateStateWithNewList(collection);
    }

    private indexOfSelectedItem(): number {
        if (this.state.list.count() === 0)
            return -1;

        for (let i = 0; i < this.state.list.count(); i++) {
            var item = this.state.list.toArray()[i];
            if (item.Active == true) {
                return i;
            }
        }
        return -1;
    }

    private updateStateWithNewList(list: SearchAddressListItem[]): void {
        this.mutateState(s => {
            s.forceHideList = false;
            s.list = Immutable.List(list);
        });
    }

    private selectActiveItem(): void {
        let item = this.state.list.filter(x => x.Active == true).toArray();
        if (item.length > 0)
            this.selectItem(item[0]);
    }

    private loadData(text: string, isSearch2: boolean, NoDebounce: boolean = false): void {
        if (this.timeoutReferenceSearch !== -1) {
            clearTimeout(this.timeoutReferenceSearch);
            this.timeoutReferenceSearch = -1;
        }

        this.timeoutReferenceSearch = window.setTimeout(() => {
            this.loadDataRaw(text, isSearch2);
        }, (NoDebounce == true) ? 50 : this.props.debounce);
    }

    private loadDataRaw(text: string, isSearchWithLastId: boolean): void {
        if (text.trim().length >= 6 && this.props.countryCode == "CA") {
            this.orderedResponseId = this.orderedResponseId + 1;

            const onSuccess = (response: any): void => {
                const tmpModels: SearchAddressResponse[] = response.Items;
                let listItems: SearchAddressListItem[];

                if (tmpModels != null) {
                    listItems = tmpModels.map(x => new SearchAddressListItem(x));

                    if (listItems.length > 0)
                        listItems[0].Active = true;
                }
                this.mutateState(s => s.list = Immutable.List(listItems));
            };

            isSearchWithLastId
                ? CanadaPostClient.GetWithLastId(text, onSuccess)
                : CanadaPostClient.GetWithSearchTerm(text, onSuccess);
        }
        else {
            this.mutateState(s => s.list = Immutable.List([]));
        }
    }

    private selectItem(item: SearchAddressListItem): void {
        if (item.model.Next == "Find") {
            this.loadData(item.model.Id, true);
            window.setTimeout(() => this.returnFocus(), 150);
        }
        else {
            let city = "";
            let postalCode = "";
            let stateCode = "";
            if (!IsNullOrWhiteSpace(item.model.Description)) {
                let splitted = item.model.Description.split(",");

                city = splitted[0];
                if (splitted.length > 2) {
                    // When the address contains a county it is in the second position of the array. Since we don't want that information, we skip it.
                    stateCode = splitted.length > 3 ? splitted[2].replaceAll(" ", "") : splitted[1].replaceAll(" ", "");
                    postalCode = splitted.length > 3 ? splitted[3].replaceAll(" ", "") : splitted[2].replaceAll(" ", "");
                }
            }

            this.mutateState(
                s => {
                    s.forceHideList = true;
                    s.text = item.model.Text;
                    s.list = Immutable.List([]);
                },
                () => this.props.onItemSelectCallback({
                    streetAddress: item.model.Text,
                    city: city,
                    postalCode: postalCode,
                    stateCode: stateCode,
                    countryCode: this.props.countryCode
                })
            );

            window.setTimeout(
                () => {
                    this.returnFocus();
                    $("#" + this.state.id).select();
                },
                150
            );
        }
    }

    private onFocus(): void {
        window.setTimeout(() => $("#" + this.state.id).select(), 50);
    }

    private onClick(event: React.MouseEvent): void {
        event.stopPropagation();

        if (this.state.forceHideList)
            this.mutateState(s => s.forceHideList = false);
    }

    private onBlur(): void {
        window.setTimeout(
            () => this.mutateState(s => s.forceHideList = true),
            150
        );
        this.props.onBlur?.();
    }

    private returnFocus(): void {
        $("#" + this.state.id).focus();
    }
}

export { SearchAddress };
